import { CheckIcon, XIcon } from 'lucide-react';

import { cn } from '@mds/shared/lib/utils';
import { Progress } from '@mds/shared/ui/shadcn/progress';

/**
 * A row's state: `done` is valid, `missing` was tried and is wrong, `idle` is
 * untouched.
 */
export type BadgeStatus = 'done' | 'missing' | 'idle';

export type BadgeRow = { label: string; status: BadgeStatus };

/**
 * Each state's mark. Idle sits a step smaller, so a row turning done or
 * missing pops up to size as it fills.
 */
const MARK = {
  done: { icon: CheckIcon, className: 'bg-brand text-on-brand' },
  missing: { icon: XIcon, className: 'bg-danger-wash text-danger-text' },
  idle: { icon: null, className: 'scale-85 bg-raised text-ink-muted' },
} as const;

export type CrewBadgeProps = {
  /** The applicant's name, live from the form. Empty shows a placeholder. */
  name?: string;
  /** The line above the name. */
  eyebrow?: string;
  rows?: BadgeRow[];
  /** 0 to 1. */
  progress?: number;
  /** Under the bar: "3 of 5 complete". */
  progressLabel?: string;
  /** Lands the "Received" stamp. */
  received?: boolean;
  className?: string;
};

/**
 * The careers page's lanyard ID card: a strap, a clip and a card that fills in
 * as the applicant completes the form, then takes a "Received" stamp when the
 * application is sent.
 *
 * Decorative. It mirrors the form beside it, which already labels every field
 * and announces every error, so the whole badge is `aria-hidden` rather than
 * reading each tick a second time.
 *
 * Swings from the top of its strap. Every animation and transition here stops
 * under reduced motion.
 */
export default function CrewBadge({
  name = '',
  eyebrow = 'Future teammate',
  rows = [],
  progress = 0,
  progressLabel,
  received = false,
  className,
}: CrewBadgeProps) {
  const shownName = name.trim();
  const percent = Math.round(Math.max(0, Math.min(1, progress)) * 100);

  return (
    <div
      aria-hidden="true"
      className={cn(
        'flex origin-top flex-col items-center',
        'animate-badge-sway motion-reduce:animate-none',
        className,
      )}
    >
      {/* The strap. Short where the badge sits under the form, long from `lg`
          where it hangs from the header. `bg-primary`, the Button's own fill,
          so on a phone, where it hangs from the Send button, the two are one
          green. */}
      <div className="h-20 w-6 rounded-b-sm bg-primary lg:h-40" />

      {/* The clip, over the strap's end. */}
      <div className="-mt-1 h-5.5 w-13.5 rounded-sm border border-ink-muted/40 bg-raised" />

      {/* Landscape below `lg`, where it sits under the form across the
          column; the spec's portrait card from `lg`, beside the form.
          The ratio is a minimum rather than a cage: with no `overflow-hidden`
          here, a long name or a wrapped row grows the card instead of being
          cut off. The header band rounds its own top corners for that reason. */}
      <div
        className={cn(
          'relative -mt-1.5 flex aspect-badge-wide w-full max-w-md flex-col',
          'lg:aspect-badge lg:w-badge',
          'rounded-card border border-hairline bg-surface text-ink shadow-lift',
        )}
      >
        {/* The punched slot the clip goes through. */}
        <div className="absolute top-3.5 left-1/2 z-1 h-2.5 w-14 -translate-x-1/2 rounded-full border border-hairline bg-page" />

        <div className="flex items-center rounded-t-card bg-panel px-5 pt-8 pb-3 lg:px-6 lg:pt-10 lg:pb-5">
          <img
            src="/images/mds-logo-color-on-dark.svg"
            alt=""
            className="block h-8 w-auto max-w-full object-contain object-left lg:h-12"
          />
        </div>

        {/* Landscape: two columns, the name over the progress on the left and
            the checklist down the right. Portrait: one column, top to bottom. */}
        <div className="grid flex-1 grid-cols-2 gap-x-5 gap-y-3 p-5 lg:flex lg:flex-col lg:gap-4 lg:p-6">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="font-mono text-xs text-brand-text uppercase">{eyebrow}</span>
            <span
              className={cn(
                'font-display text-xl wrap-anywhere lg:text-2xl',
                shownName ? 'text-ink' : 'text-ink-muted',
              )}
            >
              {shownName || 'Your name here'}
            </span>
          </div>

          <ul className="col-start-2 row-span-2 row-start-1 m-0 flex list-none flex-col gap-2 p-0 text-sm text-ink-secondary">
            {rows.map((row) => {
              const mark = MARK[row.status];
              return (
                <li
                  key={row.label}
                  className="flex items-center justify-between gap-3 border-t border-hairline pt-2"
                >
                  <span>{row.label}</span>
                  <span
                    className={cn(
                      'grid size-5.5 shrink-0 place-items-center rounded-full',
                      'transition-[background-color,scale] duration-(--duration-fast) ease-pop',
                      'motion-reduce:transition-none',
                      mark.className,
                    )}
                  >
                    {mark.icon && <mark.icon className="size-3" strokeWidth={3} />}
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="flex flex-col gap-2 self-end lg:mt-auto lg:self-auto">
            {/* The bright mint rather than the Progress default's dark green:
                the fill matches the ticks above it. */}
            <Progress
              value={percent}
              className="h-1.5 bg-raised *:data-[slot=progress-indicator]:bg-brand"
            />
            {progressLabel && (
              <span className="text-2xs font-medium text-ink-muted">{progressLabel}</span>
            )}
          </div>
        </div>

        {/* Centred across the card and two thirds down it, over the body below
            the logo band rather than the band itself. Centred with `translate`, its own property
            in Tailwind v4, so it composes with the stamp animation's `transform`
            rather than being overwritten by it. */}
        {received && (
          <div
            className={cn(
              'absolute top-2/3 left-1/2 -translate-1/2 rounded-input border-2 border-brand-edge bg-surface/85 px-4 py-2',
              'font-display text-xl tracking-wide text-brand-text uppercase',
              'animate-badge-stamp motion-reduce:animate-none motion-reduce:-rotate-12',
            )}
          >
            Received
          </div>
        )}
      </div>
    </div>
  );
}
